import { useCallback, useEffect, useRef } from "react";

/**
 * Pointer "paint a row" gesture for grid cells. A tap still toggles a single
 * cell; pressing and dragging across cells in the same row arms every cell
 * between the anchor and the current position. Releasing outside the grid (or
 * across rows) ends the gesture cleanly.
 */
export interface DragPaintCallbacks {
  onTap(row: number, col: number): void;
  onArmRange(row: number, fromCol: number, toCol: number): void;
}

export interface DragPaintHandlers {
  onCellPointerDown(
    row: number,
    col: number,
  ): (e: React.PointerEvent<HTMLElement>) => void;
  onCellPointerEnter(
    row: number,
    col: number,
  ): (e: React.PointerEvent<HTMLElement>) => void;
  onCellPointerUp(
    row: number,
    col: number,
  ): (e: React.PointerEvent<HTMLElement>) => void;
}

export function useDragPaint(cb: DragPaintCallbacks): DragPaintHandlers {
  const anchorRef = useRef<{ row: number; col: number } | null>(null);
  const hasDraggedRef = useRef(false);
  const pointerIdRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    anchorRef.current = null;
    hasDraggedRef.current = false;
    pointerIdRef.current = null;
  }, []);

  useEffect(() => {
    window.addEventListener("pointerup", clear);
    window.addEventListener("pointercancel", clear);
    return () => {
      window.removeEventListener("pointerup", clear);
      window.removeEventListener("pointercancel", clear);
    };
  }, [clear]);

  const onCellPointerDown =
    (row: number, col: number) => (e: React.PointerEvent<HTMLElement>) => {
      // Release implicit capture so pointerenter fires on neighbouring cells
      // during drag — especially on mobile Safari where touch events are
      // otherwise captured to the element first touched.
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      anchorRef.current = { row, col };
      hasDraggedRef.current = false;
      pointerIdRef.current = e.pointerId;
    };

  const onCellPointerEnter =
    (row: number, col: number) => (e: React.PointerEvent<HTMLElement>) => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current)
        return;
      if (e.pointerType === "mouse" && e.buttons === 0) {
        clear();
        return;
      }
      if (anchor.row !== row) return;
      if (anchor.col === col && !hasDraggedRef.current) return;
      hasDraggedRef.current = true;
      cb.onArmRange(row, anchor.col, col);
    };

  const onCellPointerUp =
    (row: number, col: number) => (e: React.PointerEvent<HTMLElement>) => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current)
        return;
      if (
        !hasDraggedRef.current &&
        anchor.row === row &&
        anchor.col === col
      ) {
        cb.onTap(row, col);
      }
      clear();
    };

  return { onCellPointerDown, onCellPointerEnter, onCellPointerUp };
}
